import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fmtUSD } from "@/lib/format";
import { toast } from "sonner";
import { Trash2, Calculator, Target, Scale } from "lucide-react";

export default function Saved() {
  const [shifts, setShifts] = useState([]);
  const [goals, setGoals] = useState([]);
  const [comps, setComps] = useState([]);

  const load = () => {
    api.get("/shift-calculations").then((r) => setShifts(r.data)).catch(() => {});
    api.get("/income-goals").then((r) => setGoals(r.data)).catch(() => {});
    api.get("/job-comparisons").then((r) => setComps(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const del = async (kind, id) => {
    await api.delete(`/${kind}/${id}`);
    toast.success("Deleted");
    load();
  };

  const Empty = ({ text }) => <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400">{text}</p>;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#18A6A6]">Saved</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#0F2747]">Your saved work</h1>
      </div>

      <Tabs defaultValue="shifts">
        <TabsList className="rounded-full">
          <TabsTrigger value="shifts" data-testid="tab-shifts" className="rounded-full">Shift Pay</TabsTrigger>
          <TabsTrigger value="goals" data-testid="tab-goals" className="rounded-full">Income Plans</TabsTrigger>
          <TabsTrigger value="comps" data-testid="tab-comps" className="rounded-full">Comparisons</TabsTrigger>
        </TabsList>

        <TabsContent value="shifts" className="mt-4 space-y-3">
          {shifts.length === 0 ? <Empty text="No saved shift calculations yet." /> : shifts.map((s) => (
            <div key={s.id} className="flex items-center justify-between rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <Calculator className="h-5 w-5 text-[#18A6A6]" />
                <div><p className="font-semibold text-[#0F2747]">{s.name}</p>
                  <p className="text-sm text-slate-500">Total: {fmtUSD(s.results?.total, 2)}</p></div>
              </div>
              <button data-testid={`del-shift-${s.id}`} onClick={() => del("shift-calculations", s.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="goals" className="mt-4 space-y-3">
          {goals.length === 0 ? <Empty text="No saved income plans yet." /> : goals.map((g) => (
            <div key={g.id} className="flex items-center justify-between rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <Target className="h-5 w-5 text-[#18A6A6]" />
                <div><p className="font-semibold text-[#0F2747]">{g.name}</p>
                  <p className="text-sm text-slate-500">{g.results?.shifts} shifts/mo · {fmtUSD(g.results?.annual)} / yr</p></div>
              </div>
              <button data-testid={`del-goal-${g.id}`} onClick={() => del("income-goals", g.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="comps" className="mt-4 space-y-3">
          {comps.length === 0 ? <Empty text="No saved comparisons yet." /> : comps.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <Scale className="h-5 w-5 text-[#18A6A6]" />
                <div><p className="font-semibold text-[#0F2747]">{c.name}</p>
                  <p className="text-sm text-slate-500">{fmtUSD(c.results?.a?.adjusted)} vs {fmtUSD(c.results?.b?.adjusted)}</p></div>
              </div>
              <button data-testid={`del-comp-${c.id}`} onClick={() => del("job-comparisons", c.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
